import { isAfter, isBefore, startOfDay, endOfDay } from 'date-fns'
import { IOrderItem, IDiscountItemMap } from '../../model/order.store'
import { keys, sumBy, get, forEach, includes, orderBy } from 'lodash'
import { getItemSoldPrice } from '../order.util'

/**
 * Chương trình sale Socola 8/3/2026:
 * 1. Mua 2 bánh hộp thiếc tặng 1 socola Truffle (free 100%)
 * 2. Mỗi hộp thiếc còn lại được mua 1 socola giá ưu đãi (59k/69k)
 * 3. Socola còn lại sale 30%
 */

// Định nghĩa giá sale 30% và giá ưu đãi kèm hộp thiếc
const CHOCOLATE_CONFIG: { [sku: string]: { salePrice: number; tinBoxPrice: number } } = {
  // Truffle 9
  V2204: { salePrice: 111000, tinBoxPrice: 59000 },
  V10687: { salePrice: 111000, tinBoxPrice: 59000 },
  // Truffle 12
  V2205: { salePrice: 132000, tinBoxPrice: 69000 },
  V10686: { salePrice: 132000, tinBoxPrice: 69000 },
  // Nama 9
  V10688: { salePrice: 132000, tinBoxPrice: 69000 },
  V10689: { salePrice: 132000, tinBoxPrice: 69000 }
  // Nama 12
  // V10769: { salePrice: 139000, tinBoxPrice: 69000 },
  // V10770: { salePrice: 139000, tinBoxPrice: 69000 },
}

// Danh sách SKU socola được tặng free khi mua 2 bánh hộp thiếc (chỉ Truffle)
const GIFT_ELIGIBLE_SKUS = [
  'V2204',  // Hộp truffle 9 viên kèm túi & thiệp màu loang
  'V2205',  // Hộp truffle 12 viên kèm túi & thiệp màu loang
  'V10687', // Hộp truffle 9 viên kèm túi & thiệp màu đỏ
  'V10686'  // Hộp truffle 12 viên kèm túi & thiệp màu đỏ
]

// Config cho chương trình mua 2 bánh hộp thiếc tặng 1 socola
const TIN_BOX_GIFT_CONFIG = {
  buyQuantity: 2, // Mua 2 bánh hộp thiếc
  giftQuantity: 1 // Tặng 1 socola
}

export const CHOCOLATE_MARCH_2026_TIME_FRAME = {
  start: startOfDay(new Date('2026-03-01')),
  end: endOfDay(new Date('2026-03-08'))
}

interface IProcessChocolateSaleParams {
  items: IOrderItem[];
  resultMap: IDiscountItemMap;
  orderPlatform: string;
  orderType: string;
}

/**
 * Xử lý chương trình sale Socola 8/3/2026
 * Thứ tự ưu tiên:
 * 1. Free 100%: Mua 2 bánh hộp thiếc tặng 1 socola Truffle
 * 2. Giá ưu đãi (59k/69k): Mỗi hộp thiếc còn lại được mua 1 socola
 * 3. Sale 30%: Socola còn lại
 */
export function processChocolateMarch2026Sale({
  items,
  resultMap,
  orderPlatform,
  orderType
}: IProcessChocolateSaleParams): void {
  // Chỉ áp dụng cho đơn trực tiếp (SAVOR_SHOP)
  if (orderPlatform !== 'SAVOR_SHOP') {
    return
  }

  // Kiểm tra thời gian từ 1/3 đến 8/3
  const now = new Date()
  if (isBefore(now, CHOCOLATE_MARCH_2026_TIME_FRAME.start) || isAfter(now, CHOCOLATE_MARCH_2026_TIME_FRAME.end)) {
    return
  }

  const chocolateSkus = keys(CHOCOLATE_CONFIG)

  // Đếm số lượng bánh hộp thiếc
  const tinBoxQuantity = sumBy(items, (item) => {
    const matchTag = (get(item, 'product.tag', '') || get(item, 'tag', '')).match(/Bánh hộp thiếc/)
    const matchSubCat =
      get(item, 'product.category.subCat.name', get(item, 'category.subCat.name', '')) === 'Bánh hộp thiếc'
    const matchSubCatWorkName = get(item, 'subCatWorkName', '') === 'Bánh hộp thiếc'
    if (matchTag || matchSubCat || matchSubCatWorkName) {
      return item.quantity
    }
    return 0
  })

  // Tính số lượng socola được tặng free: mua 2 bánh hộp thiếc tặng 1 socola Truffle
  const numFreeChocolates = Math.floor(tinBoxQuantity / TIN_BOX_GIFT_CONFIG.buyQuantity) * TIN_BOX_GIFT_CONFIG.giftQuantity
  // Số hộp thiếc còn lại dùng cho giá ưu đãi
  const remainingTinBoxForPrice = tinBoxQuantity % TIN_BOX_GIFT_CONFIG.buyQuantity

  // eslint-disable-next-line no-console
  console.log('[Socola 8326] Bắt đầu xử lý:', {
    tinBoxQuantity,
    numFreeChocolates,
    remainingTinBoxForPrice,
    items: items.map((item) => ({
      id: item.id,
      sku: item.sku,
      description: item.description,
      quantity: item.quantity,
      normalPrice: item.normalPrice,
      salePrice: item.salePrice
    })),
    resultMapBefore: Object.keys(resultMap).map((key) => ({
      id: key,
      discount: resultMap[key].discount,
      saleProgram: resultMap[key].saleProgram
    }))
  })

  // Expand chocolate items để xử lý từng cái riêng lẻ - track theo itemId
  const expandedChocolateItems: { sku: string; soldPrice: number; itemId: string }[] = []
  forEach(items, (item) => {
    if (includes(chocolateSkus, item.sku)) {
      const soldPrice = getItemSoldPrice({
        item,
        orderPlatform,
        orderType
      })
      for (let i = 0; i < item.quantity; i++) {
        expandedChocolateItems.push({
          sku: item.sku,
          soldPrice,
          itemId: item.id
        })
      }
    }
  })

  if (expandedChocolateItems.length === 0) {
    return
  }

  // Sort theo giá tăng dần (ưu tiên socola rẻ được hưởng ưu đãi trước)
  const sortedChocolateItems = orderBy(expandedChocolateItems, ['soldPrice'], ['asc'])

  // Tính discount cho từng sản phẩm, track theo itemId
  const discountByItemId: { [itemId: string]: number } = {}
  const freeCountByItemId: { [itemId: string]: number } = {}
  const tinBoxCountByItemId: { [itemId: string]: number } = {}
  const saleCountByItemId: { [itemId: string]: number } = {}

  let remainingFree = numFreeChocolates
  let remainingTinBoxBonus = remainingTinBoxForPrice

  forEach(sortedChocolateItems, (chocolateItem) => {
    const config = CHOCOLATE_CONFIG[chocolateItem.sku]
    if (!config) {
      return
    }

    let discount: number = 0
    const isGiftEligible = includes(GIFT_ELIGIBLE_SKUS, chocolateItem.sku)

    let discountType = ''
    if (isGiftEligible && remainingFree > 0) {
      // Ưu tiên 1: Được tặng free (chỉ Truffle)
      discount = chocolateItem.soldPrice
      remainingFree--
      freeCountByItemId[chocolateItem.itemId] = get(freeCountByItemId, chocolateItem.itemId, 0) + 1
      discountType = 'FREE'
    } else if (remainingTinBoxBonus > 0) {
      // Ưu tiên 2: Giá ưu đãi hộp thiếc (59k/69k)
      discount = chocolateItem.soldPrice - config.tinBoxPrice
      remainingTinBoxBonus--
      tinBoxCountByItemId[chocolateItem.itemId] = get(tinBoxCountByItemId, chocolateItem.itemId, 0) + 1
      discountType = 'TIN_BOX_PRICE'
    } else {
      // Ưu tiên 3: Sale 30%
      discount = chocolateItem.soldPrice - config.salePrice
      saleCountByItemId[chocolateItem.itemId] = get(saleCountByItemId, chocolateItem.itemId, 0) + 1
      discountType = 'SALE_30'
    }

    // eslint-disable-next-line no-console
    console.log('[Socola 8326] Xử lý item:', {
      sku: chocolateItem.sku,
      itemId: chocolateItem.itemId,
      soldPrice: chocolateItem.soldPrice,
      discountType,
      discount,
      isGiftEligible,
      remainingFree,
      remainingTinBoxBonus
    })

    if (discount > 0) {
      discountByItemId[chocolateItem.itemId] = get(discountByItemId, chocolateItem.itemId, 0) + discount
    }
  })

  // Áp dụng discount vào resultMap với label phân biệt rõ loại ưu đãi
  forEach(items, (item) => {
    const discount = get(discountByItemId, item.id, 0)
    if (discount > 0) {
      resultMap[item.id].discount = get(resultMap[item.id], 'discount', 0) + discount

      // Xác định label dựa trên loại ưu đãi ưu tiên nhất của item
      const freeCount = get(freeCountByItemId, item.id, 0)
      const tinBoxCount = get(tinBoxCountByItemId, item.id, 0)
      const saleCount = get(saleCountByItemId, item.id, 0)

      // Ưu tiên hiển thị: free > ưu đãi hộp thiếc > sale 30%
      if (freeCount > 0) {
        resultMap[item.id].saleProgram = 'Giảm giá Socola 8326 - Tặng free'
      } else if (tinBoxCount > 0) {
        resultMap[item.id].saleProgram = 'Giảm giá Socola 8326 - Ưu đãi hộp thiếc'
      } else {
        resultMap[item.id].saleProgram = 'Giảm giá Socola 8326 - Sale 30%'
      }

      // eslint-disable-next-line no-console
      console.log('[Socola 8326] Áp dụng discount:', {
        itemId: item.id,
        sku: item.sku,
        quantity: item.quantity,
        discount,
        freeCount,
        tinBoxCount,
        saleCount,
        saleProgram: resultMap[item.id].saleProgram
      })
    }
  })

  // eslint-disable-next-line no-console
  console.log('[Socola 8326] Kết quả resultMap:', items.map((item) => ({
    id: item.id,
    sku: item.sku,
    description: item.description,
    quantity: item.quantity,
    discount: resultMap[item.id].discount,
    saleProgram: resultMap[item.id].saleProgram
  })))
}
